import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAgentDraftCart from '../../hooks/useAgentDraftCart';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';
import { Trash2, Plus, Minus, Percent, DollarSign, ShoppingCart, FileText, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

/**
 * AgentCartPage - Full page cart for field agents (LUM-15)
 * Replaces the CartSidebar overlay with a dedicated page
 */
const AgentCartPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const {
    cart, updateQuantity, updatePrice, updateNotes,
    removeItem, clearCart, getTotalItems, isLoading
  } = useAgentDraftCart(companyId);

  const [companyName, setCompanyName] = useState('');
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState(0);
  const [discountReason, setDiscountReason] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [editingPrice, setEditingPrice] = useState(null);
  const [tempPrice, setTempPrice] = useState({});
  const [itemDiscountModal, setItemDiscountModal] = useState(null);
  const [itemDiscountPercent, setItemDiscountPercent] = useState('');
  const [isDiscountOpen, setIsDiscountOpen] = useState(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch company name
  useEffect(() => {
    if (companyId) {
      axios.get(`/api/field-agent/companies/${companyId}`)
        .then(res => setCompanyName(res.data.company?.name || res.data.name || ''))
        .catch(() => {});
    }
  }, [companyId]);

  const getItemId = (item) => `${item.product_id}-${item.is_tester ? 'tester' : ''}-${item.is_bonus ? 'bonus' : ''}`;

  const toggleNote = (itemId) => {
    setExpandedNotes(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // Calculate totals
  const subtotal = cart.items.reduce((sum, item) => {
    return sum + (parseFloat(item.price) * parseInt(item.quantity));
  }, 0);

  const calculateDiscount = () => {
    const value = parseFloat(discountValue) || 0;
    return discountType === 'percentage' ? (subtotal * value) / 100 : value;
  };

  const discount = calculateDiscount();
  const total = subtotal - discount;

  const handlePriceEdit = (productId, currentPrice) => {
    setEditingPrice(productId);
    setTempPrice({ ...tempPrice, [productId]: currentPrice });
  };

  const handlePriceSave = (productId) => {
    const newPrice = parseFloat(tempPrice[productId]);
    if (newPrice > 0) {
      updatePrice(productId, newPrice);
      toast.success('מחיר עודכן בהצלחה');
      setEditingPrice(null);
    } else {
      toast.error('מחיר לא תקין');
    }
  };

  const handleItemDiscount = (productId, currentPrice) => {
    setItemDiscountModal({ productId, originalPrice: currentPrice });
    setItemDiscountPercent('');
  };

  const applyItemDiscount = () => {
    const percent = parseFloat(itemDiscountPercent);
    if (!percent || percent <= 0 || percent >= 100) {
      toast.error('אחוז הנחה לא תקין (1-99)');
      return;
    }
    const item = cart.items.find(i => i.product_id === itemDiscountModal.productId);
    if (!item) return;
    const originalPrice = item.originalPrice || item.price;
    const newPrice = originalPrice * (1 - percent / 100);
    updatePrice(itemDiscountModal.productId, newPrice, { originalPrice, discountPercent: percent });
    toast.success(`הנחה ${percent}% הוחלה`);
    setItemDiscountModal(null);
  };

  const handleCheckout = async () => {
    if (cart.items.length === 0) {
      toast.error('העגלה ריקה');
      return;
    }
    if (discount > 0 && !discountReason.trim()) {
      toast.error('נא להזין סיבה להנחה');
      return;
    }

    setIsSubmitting(true);
    try {
      const orderData = {
        company_id: companyId,
        items: cart.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: parseFloat(item.price) || 0,
          notes: item.notes || '',
          is_tester: item.is_tester || false,
          is_bonus: item.is_bonus || false
        })),
        notes: orderNotes,
        payment_method: 'company_credit',
        status: 'pending',
        discount: discount || 0,
        discountType,
        discountValue: parseFloat(discountValue) || 0,
        discountReason: discountReason.trim()
      };

      const response = await axios.post('/api/field-agent/orders', orderData);
      toast.success('ההזמנה נוצרה בהצלחה!');
      await clearCart();
      navigate(`/agent/orders/${response.data.order.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.message || 'שגיאה ביצירת ההזמנה');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowRight className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="h-6 w-6 text-primary-600" />
              עגלת קניות
            </h1>
            {companyName && (
              <p className="text-sm text-gray-500 mt-1">{companyName}</p>
            )}
          </div>
        </div>
        <div className="text-sm text-gray-500">
          {getTotalItems()} פריטים
        </div>
      </div>

      {cart.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <ShoppingCart className="h-20 w-20 mb-4" />
          <p className="text-xl mb-4">העגלה ריקה</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            חזרה למוצרים
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Cart Items */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
            {cart.items.map((item, index) => {
              const itemId = getItemId(item);
              const hasNotes = item.notes && item.notes.length > 0;
              const isNoteVisible = expandedNotes[itemId] || hasNotes;

              return (
                <div key={`${itemId}-${index}`} className="p-4">
                  {/* Row 1: Name + Trash */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate" title={item.product_name}>
                        {item.product_name}
                        {item.is_tester && <span className="text-xs text-purple-600 mr-2">(טסטר)</span>}
                        {item.is_bonus && <span className="text-xs text-green-600 mr-2">(בונוס)</span>}
                      </h3>
                      <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                    </div>
                    <button
                      onClick={() => removeItem(item.product_id, item.is_tester || false, item.is_bonus || false)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Row 2: Quantity + Total Price */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.is_tester || false, item.is_bonus || false)}
                        className="p-1 border border-gray-300 rounded hover:bg-gray-100 h-8 w-8 flex items-center justify-center"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value) || 1, item.is_tester || false, item.is_bonus || false)}
                        className="w-14 text-center border border-gray-300 rounded py-1 text-sm h-8"
                      />
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.is_tester || false, item.is_bonus || false)}
                        className="p-1 border border-gray-300 rounded hover:bg-gray-100 h-8 w-8 flex items-center justify-center"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="text-lg font-bold text-primary-600">
                      ₪{(parseFloat(item.price) * item.quantity).toFixed(2)}
                    </div>
                  </div>

                  {/* Row 3: Unit Price + Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <span>₪{parseFloat(item.price).toFixed(2)} ליח'</span>
                        {item.originalPrice && item.originalPrice !== item.price && (
                          <span className="text-gray-400 line-through text-[10px]">
                            ₪{parseFloat(item.originalPrice).toFixed(2)}
                          </span>
                        )}
                      </div>
                      {item.discountPercent && (
                        <span className="text-[10px] text-yellow-600 font-medium">-{item.discountPercent}%</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {editingPrice === item.product_id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="0.01"
                            value={tempPrice[item.product_id] || item.price}
                            onChange={(e) => setTempPrice({ ...tempPrice, [item.product_id]: e.target.value })}
                            className="w-20 border border-primary-500 rounded px-2 py-1 text-xs"
                            autoFocus
                          />
                          <button
                            onClick={() => handlePriceSave(item.product_id)}
                            className="text-xs bg-primary-600 text-white px-2 py-1 rounded"
                          >
                            שמור
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => handlePriceEdit(item.product_id, item.price)}
                            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded"
                            title="ערוך מחיר"
                          >
                            <DollarSign className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleItemDiscount(item.product_id, item.price)}
                            className="p-2 text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded"
                            title="הנחה"
                          >
                            <Percent className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => toggleNote(itemId)}
                            className={`p-2 rounded ${hasNotes ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                            title="הערה"
                          >
                            <FileText className="h-5 w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Item Notes */}
                  {isNoteVisible && (
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="הערות למוצר..."
                        value={item.notes || ''}
                        onChange={(e) => updateNotes(item.product_id, e.target.value, item.is_tester || false, item.is_bonus || false)}
                        className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:ring-1 focus:ring-primary-500"
                        autoFocus={!hasNotes}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Discount Controls - Collapsible */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setIsDiscountOpen(!isDiscountOpen)}
              className="w-full flex items-center justify-between p-4 hover:bg-yellow-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-yellow-600" />
                <h3 className="font-semibold text-sm">הנחה כללית</h3>
                {discount > 0 && !isDiscountOpen && (
                  <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">
                    {discountType === 'percentage' ? `${discountValue}%` : `₪${discountValue}`}
                  </span>
                )}
              </div>
              {isDiscountOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {isDiscountOpen && (
              <div className="p-4 border-t border-yellow-200">
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setDiscountType('percentage')}
                    className={`flex-1 py-2 px-3 rounded text-sm font-medium ${discountType === 'percentage' ? 'bg-yellow-600 text-white' : 'bg-white text-gray-700'}`}
                  >
                    אחוזים (%)
                  </button>
                  <button
                    onClick={() => setDiscountType('fixed')}
                    className={`flex-1 py-2 px-3 rounded text-sm font-medium ${discountType === 'fixed' ? 'bg-yellow-600 text-white' : 'bg-white text-gray-700'}`}
                  >
                    סכום קבוע (₪)
                  </button>
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="number"
                    min="0"
                    step={discountType === 'percentage' ? '1' : '0.01'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'percentage' ? 'אחוזים' : 'סכום'}
                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                  <button
                    onClick={() => { setDiscountValue(0); setDiscountReason(''); }}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                  >
                    נקה
                  </button>
                </div>
                {discount > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-yellow-800 mb-1">
                      סיבת הנחה <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={discountReason}
                      onChange={(e) => setDiscountReason(e.target.value)}
                      placeholder="למשל: לקוח VIP, מבצע מיוחד, פיצוי..."
                      className="w-full border border-yellow-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Notes - Collapsible */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setIsNotesOpen(!isNotesOpen)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-600" />
                <h3 className="font-semibold text-sm">הערות להזמנה</h3>
                {orderNotes && !isNotesOpen && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">יש הערות</span>
                )}
              </div>
              {isNotesOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {isNotesOpen && (
              <div className="p-4 border-t border-gray-200 bg-white">
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="הערות כלליות להזמנה..."
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                />
              </div>
            )}
          </div>

          {/* Totals + Checkout */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">סכום ביניים:</span>
              <span className="font-semibold">₪{subtotal.toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-yellow-600">
                <span>הנחה ({discountType === 'percentage' ? `${discountValue}%` : 'סכום קבוע'}):</span>
                <span className="font-semibold">-₪{discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-xl font-bold border-t pt-3">
              <span>סה"כ לתשלום:</span>
              <span className="text-primary-600">₪{total.toFixed(2)}</span>
            </div>
            <button
              onClick={handleCheckout}
              disabled={isSubmitting}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white py-3 rounded-lg font-bold text-lg transition-colors flex items-center justify-center gap-2"
            >
              <ShoppingCart className="h-5 w-5" />
              {isSubmitting ? 'שולח...' : 'השלם הזמנה'}
            </button>
          </div>
        </div>
      )}

      {/* Item Discount Modal */}
      {itemDiscountModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setItemDiscountModal(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">הנחה באחוזים למוצר</h3>
              <p className="text-sm text-gray-600 mb-4">
                מחיר מקורי: ₪{parseFloat(itemDiscountModal.originalPrice).toFixed(2)}
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">אחוז הנחה (1-99)</label>
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={itemDiscountPercent}
                  onChange={(e) => setItemDiscountPercent(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="10"
                  autoFocus
                />
                {itemDiscountPercent && (
                  <p className="text-xs text-gray-500 mt-1">
                    מחיר חדש: ₪{(itemDiscountModal.originalPrice * (1 - parseFloat(itemDiscountPercent || 0) / 100)).toFixed(2)}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={applyItemDiscount} className="flex-1 bg-yellow-600 text-white py-2 rounded hover:bg-yellow-700">
                  החל הנחה
                </button>
                <button onClick={() => setItemDiscountModal(null)} className="flex-1 bg-gray-200 text-gray-700 py-2 rounded hover:bg-gray-300">
                  ביטול
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AgentCartPage;
